/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { FileOpener } from "@capacitor-community/file-opener";
import { Capacitor } from "@capacitor/core";
import { Directory } from "@capacitor/filesystem";
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonImg,
  IonPage,
  IonSpinner,
  IonToolbar,
} from "@ionic/react";
import { Document, pdf } from "@react-pdf/renderer";
import WriteBlob from "capacitor-blob-writer";
import { format, subYears } from "date-fns";
import saveAs from "file-saver";
import _ from "lodash";
import mime from "mime";
import { ConfidenceLevelGraph, DetailsCard, ProgressCheckButton } from "parafolio-components";
import { PMUsageData, ProgramModule } from "pf-support-lib";
import { useCallback, useEffect, useMemo, useState } from "react";
import { EventRegister } from "react-native-event-listeners";
import { useHistory, useParams } from "react-router";
import { useRecoilValue, useRecoilValueLoadable } from "recoil";
import {
  Button_Info_White,
  Icon_Add_Evidence,
  Icon_Export_Blue,
  Icon_Pencil,
  Icon_Refresh,
  Image_Completion,
} from "@assets/images";
import CustomProgressCheckButton from "@components/common/CustomProgressCheckButton";
import HeaderTitle from "@components/common/HeaderTitle";
import EvidenceContainer from "@components/evidence/EvidenceContainer";
import ProgramInfoCard from "@components/home/ProgramInfoCard";
import ExportedPDF from "@components/pdfExport/ExportedPDF";
import CompetenceItem from "@components/programs/CompetenceItem";
import DetailsContentCollapsible from "@components/programs/DetailsContentCollapsible";
import InfoModal from "@components/programs/InfoModal";
import MandatoryCertificateDisplay from "@components/programs/MandatoryCertificateDisplay";
import NOSOverviewItem from "@components/programs/NOSOverviewItem";
import ProgramCardButton from "@components/programs/ProgramCardButton";
import ProgramCertificatesDisplay from "@components/programs/ProgramCertificatesDisplay";
import ProgramEvidenceCard from "@components/programs/ProgramEvidenceCard";
import ProgramRolesDisplay from "@components/programs/ProgramRolesDisplay";
import RequiredSkillCard from "@components/programs/RequiredSkillCard";
import AWSService from "@controllers/AWSService";
import DataController from "@controllers/DataController";
import * as FirebaseService from "@controllers/FirebaseService";
import { useDebugUser } from "@hooks/auth/useDebugUser";
import { useProgramData } from "@hooks/data/useProgramData";
import { useProgramEvidence } from "@hooks/programs/useProgramEvidence";
import { useProgramInfo } from "@hooks/programs/useProgramInfo";
import { useProgramSkills } from "@hooks/programs/useProgramSkills";
import { useProgressCheckData } from "@hooks/programs/useProgressCheckData";
import {
  AWSAccessParams,
  AllProgressCheckStatuses,
  CompetenceActivityClass,
  ConfidenceLevelData,
  IEvidence,
  IExportType,
  EvidenceDefinition,
  IUser,
  IUserProgramCertificate,
  ProgramCertificate,
  ProgramData,
  ProgramSkill,
  ProgressCheck,
  Required,
  UsageData,
  ProgressCheckStatus,
} from "@interfaces/Interfaces";
import { evidenceAtom, progressCheckDataAtom, userAtom } from "@state/State";
import * as CommentHelpers from "@utils/commentHelpers";
import * as ProgramUtils from "@utils/programUtils";
import * as StringUtils from "@utils/stringUtils";
import LasDetails from "./LasDetails";
import MonthlyEvidenceBreakdown from "@components/evidence/MonthlyEvidenceBreakdown";
import ProgramInfoCardButton from "@components/home/ProgramInfoCardButton";
import NQPLearningOutcomesDisplay from "@components/programs/NQPLearningOutcomesDisplay";
import { useProgramModuleStatus } from "@hooks/programs/useProgramModuleStatus";
import EvidenceDisclaimer from "@components/programs/EvidenceDisclaimer";
import { TRUST_BACKGROUND_COLOUR_VARIANTS } from "../../Constants";
import ProgramSkillsCard from "@components/programs/ProgramSkillsCard";
import { COMPETENCY_IDS, PROGRESS_CHECK_IDS, UOC_PROGRAM_EVIDENCE_DEFINITION_IDS } from "@constants/programs";
import MedicalImagingDisplay from "@components/programs/MedicalImagingDisplay";
import { useRefreshProgramData } from "@hooks/programs/useRefreshProgramData";
import ProgramRoleRequiredSkillCard from "@components/programs/ProgramRoleRequiredSkillCard";
import ProgramCertificatesCardDisplay from "@components/programs/ProgramCertificatesCardDisplay";
import ProgramSkillsRAGCard from "@components/programs/ProgramSkillsRAGCard";
import { useProgramCycles } from "@hooks/programs/useProgramCycles";
import * as ProgressCheckService from "@controllers/ProgressCheckService";
import { useRefreshProgressCheckData } from "@hooks/data/useRefreshProgressCheckData";
import { useProgramCycleEvidence } from "@hooks/programs/useProgramCycleEvidence";
import { userRolesSelector } from "@state/UserState";
import WPBACardDisplay from "@components/programs/WPBACardDisplay";
import ProgramSkillsMap from "@components/programs/ProgramSkillsMap";

const ProgramHome: React.FC = () => {
  const history = useHistory();
  const params = useParams<{ programId: string }>();

  const { userPrograms, getProgramStartDate } = useProgramData();
  const { isRefreshingProgramData, refreshUserProgramData } = useRefreshProgramData();
  const { refreshProgressCheckData } = useRefreshProgressCheckData();
  const { isDebugUser } = useDebugUser();
  const program: ProgramData = userPrograms.find((item) => item.ID === params.programId)!;
  const allUserRoles = useRecoilValue(userRolesSelector);

  const { currentProgramCycle } = useProgramCycles(params.programId);

  const currentProgram = useProgramInfo(params.programId);
  const programSkills = useProgramSkills(params.programId);
  const programEvidence = useProgramEvidence(currentProgram.program);
  const programCycleEvidence = useProgramCycleEvidence(currentProgram.program);

  const { programProgressCheckData } = useProgressCheckData(params.programId);
  const { programModuleStatus, fastTrackActive, pauseAndHold } = useProgramModuleStatus(params.programId);

  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const evidence = useRecoilValueLoadable<IEvidence[] | null>(evidenceAtom);
  const allProgressCheckData = useRecoilValueLoadable<AllProgressCheckStatuses[] | null>(progressCheckDataAtom);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [requiredSkills, setRequiredSkills] = useState<ProgramSkill[] | any[]>([]);
  const [certificates, setCertificates] = useState<ProgramCertificate[] | any[]>([]);
  const [currentProgressCheckData, setCurrentProgressCheckData] = useState<AllProgressCheckStatuses | null>(null);

  const [infoModalVisible, setInfoModalVisible] = useState<boolean>(false);

  const [isSetup, setIsSetup] = useState<boolean>(false);
  const [userRoles, setUserRoles] = useState<string[]>([]);

  const [declarationDate, setDeclarationDate] = useState<string>("");

  const [exportingEvidence, setExportingEvidence] = useState<boolean>(false);
  const [currentExportType, setCurrentExportType] = useState<string>("");

  const [programCertificates, setProgramCertificates] = useState<IUserProgramCertificate[] | any[]>([]);

  const [viewingCertificate, setViewingCertificate] = useState<boolean>(false);
  const [viewingCertificateFilename, setViewingCertificateFilename] = useState<string>("");

  const [usageData, setUsageData] = useState<UsageData[]>([]);
  const [updatingProgressCheckCycle, setUpdatingProgressCheckCycle] = useState<boolean>(false);

  const confidenceLevelData: ConfidenceLevelData | null = useMemo(() => {
    const confidenceLevelEvidence = ProgramUtils.getConfidenceLevelEvidence(programEvidence, program.ID);
    let mappedData: ConfidenceLevelData | null = null;

    mappedData = ProgramUtils.mapConfidenceLevelEvidence(confidenceLevelEvidence, params.programId);

    return mappedData;
  }, [programEvidence, program.ID]);

  const isUOCProgramLocked = useMemo(
    () =>
      ProgramUtils.isUOCProgramLockedByEoYReview(
        program.ID,
        currentProgram.progressChecks,
        allProgressCheckData.contents,
        evidence.contents ?? [],
        ProgramUtils.getProgramLevel(user.contents?.programRoles ?? [], program.ID) ?? ""
      ),
    [program.ID, currentProgram.progressChecks, allProgressCheckData, evidence, user]
  );

  const isUOCProgramComplete = useMemo(() => {
    if (!ProgramUtils.isUOCApprenticeship(program.ID)) return false;
    const completionPC = currentProgram.progressChecks.find((pc) => pc.ApprovalCompletesPM);
    if (!completionPC) return false;
    const dataForProgram = allProgressCheckData.contents?.find(
      (item: AllProgressCheckStatuses) => item.programID === program.ID
    );
    return (
      dataForProgram?.pCs.find((pc: ProgressCheckStatus) => pc.pCId === completionPC.ID)?.submissions.status ===
      "Approved"
    );
  }, [program.ID, currentProgram.progressChecks, allProgressCheckData]);

  const infoClicked = useCallback((): void => {
    FirebaseService.logEvent("program_info_pressed", {
      program: currentProgram.program?.Name,
    });

    setInfoModalVisible(true);
  }, [currentProgram.program]);

  useEffect(() => {
    const getProgram = (): void => {
      if (program) {
        const allCompetences: CompetenceActivityClass[] | any[] = program.CompetenceActivity ?? [];

        if (allCompetences.length > 0) {
          const required = ProgramUtils.getAllRequiredSkillsForProgram(allCompetences);

          if (required) {
            setRequiredSkills(required);
          }
        }
      }
    };

    getProgram();
  }, [evidence, program?.version]);

  useEffect(() => {
    const getProgressCheckData = (): void => {
      const dataForProgram: AllProgressCheckStatuses = allProgressCheckData.contents?.find(
        (item: any): item is AllProgressCheckStatuses => item.programID === params.programId
      );

      dataForProgram && setCurrentProgressCheckData(dataForProgram);
    };

    if (allProgressCheckData.state === "hasValue") {
      getProgressCheckData();
    }
  }, [allProgressCheckData, currentProgram.program, currentProgram.progressChecks]);

  useEffect(() => {
    const programOrder = (item: IUserProgramCertificate): number => {
      const certificateName = item?.certificate?.certificateType;
      const mappedCertificate = currentProgram.program?.Certificates?.find(
        (_cert: ProgramCertificate) => _cert.Name.toUpperCase() === certificateName?.toUpperCase()
      );

      return mappedCertificate?.Required === Required.Mandatory ? 2 : 1;
    };

    const getCertificateInfo = (): void => {
      if (program) {
        const competences = program.CompetenceActivity ?? [];

        const filteredCompetences = competences.filter(
          (item) => item && item.Certificates && item.Certificates.length > 0
        );

        const mappedCompetences = filteredCompetences.map((item: CompetenceActivityClass) => item && item.Certificates);
        const array = mappedCompetences.filter((item) => item !== undefined);
        let _certificates = _.flatten(array);

        _certificates = _certificates.slice(0, 3);
        setCertificates(_certificates);
      }
    };

    const getUserProgramCertificates = (): void => {
      if (user.contents) {
        let _programCertificates = user.contents?.programCertificates || [];
        _programCertificates = _programCertificates.filter(
          (item: IUserProgramCertificate) =>
            item.programData.programName === currentProgram.program?.Name && !item.programData.role
        );

        setProgramCertificates(
          _.orderBy(
            _programCertificates,
            [programOrder, (item) => item.certificate?.certificateType ?? item.programData.certificateType],
            ["desc", "asc"]
          )
        );
      }
    };

    if (user.state === "hasValue" && currentProgram.program) {
      getCertificateInfo();
      getUserProgramCertificates();
    }
  }, [currentProgram.program, user]);

  useEffect(() => {
    const checkIsSetup = (): void => {
      const roles = user.state === "hasValue" && _.cloneDeep(user.contents?.programRoles);

      if (roles) {
        const programRoles = roles.find((item) => item.programName === currentProgram.program.Name);

        if (programRoles) {
          if (ProgramUtils.usingDescriptiveProgramRolesFormat(params.programId)) {
            const roles = programRoles.programRoles.map((r) => r.roleName);
            setUserRoles(roles);
            setDeclarationDate(programRoles?.declarationTickDate ?? "");
          } else {
            setUserRoles(programRoles?.programRoles ?? []);
          }

          setIsSetup(true);
        }
      }
    };

    if (user.state === "hasValue" && user.contents && currentProgram.program) {
      checkIsSetup();
    }
  }, [user, currentProgram.program]);

  useEffect(() => {
    const calculateUsageData = () => {
      if (!currentProgram.program || !ProgramUtils.shouldCalculateUsageData(params.programId)) return;

      const progFound: any = userPrograms.find((p) => p.ID === program.ID);
      const pm = new ProgramModule(progFound);

      const contactID = Number(user.contents.userData.contactID);
      const roleID = ProgramUtils.getSelectedRoleIdForEPRRProgram(user.contents, program.ID);

      const pMUsageData = new PMUsageData(progFound, []);

      const filteredEvidence = programEvidence.filter((ev) => !ev.draft) as [];

      const results = pMUsageData.getUsageDataByUser(pm, contactID, roleID, filteredEvidence);

      setUsageData(results);
    };

    calculateUsageData();
  }, [JSON.stringify(programEvidence)]);

  const addProgramEvidence = (params?: {
    type?: "skill" | "standard";
    preselectedSkill?: ProgramSkill;
    nonCompliant?: boolean;
  }): void => {
    let data: any = {};

    const usesAdditionalEvidenceDefinitions = currentProgram.program.UsesAdditionalEvidenceDefinitions;
    const evidenceDefinitions = currentProgram.program["Evidence Definitions"] ?? [];
    const mandatoryStandards = ProgramUtils.getMandatoryStandards(user.contents, currentProgram.program);
    const optionalStandards = ProgramUtils.getOptionalStandards(user.contents, currentProgram.program);
    const skills = ProgramUtils.getSkillsForRole(user.contents, currentProgram.program);

    if (params?.type === "skill") {
      let definition;

      if (params?.nonCompliant) {
        definition = evidenceDefinitions?.find(
          (item: EvidenceDefinition) => item.EvidenceSelectionType === "witness-simulation"
        );
      } else {
        definition = evidenceDefinitions?.[0];
      }

      data = {
        ...data,
        program: currentProgram.program,
        ...(currentProgram.competences.length > 0 && { competences: currentProgram.competences }),
        ...(definition &&
          usesAdditionalEvidenceDefinitions && {
            evidenceDefinitions: definition,
          }),
        ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
        ...(optionalStandards.length > 0 && { optionalStandards }),
        ...(skills.length > 0 && { skills }),
        ...(params?.preselectedSkill && { preselectedSkill: params.preselectedSkill }),
        skill: null,
      };
    } else {
      data = {
        ...data,
        program: currentProgram.program,
        ...(currentProgram.competences.length > 0 && { competences: currentProgram.competences }),
        ...(evidenceDefinitions.length > 0 &&
          !usesAdditionalEvidenceDefinitions && {
            evidenceDefinitions: evidenceDefinitions[0],
          }),
        ...(currentProgram.progressChecks.length > 0 && { progressChecks: currentProgram.progressChecks }),
        ...(mandatoryStandards.length > 0 && { mandatoryStandards }),
        ...(optionalStandards.length > 0 && { optionalStandards }),
        ...(skills.length > 0 && { skills }),
        ...(params?.preselectedSkill && { preselectedSkill: params.preselectedSkill }),
        skill: null,
      };
    }

    FirebaseService.logEvent("program_add_enhanced_evidence", {
      program: currentProgram.program?.Name,
    });

    EventRegister.emit("evidence/add-program-evidence", data);
  };

  const addRequiredSkillEvidence = (competence: CompetenceActivityClass, skill: ProgramSkill): void => {
    const data = {
      program: currentProgram.program,
      competences: currentProgram.competences,
      competence: competence,
      skill: skill,
    };

    FirebaseService.logEvent("program_required_skill_evidence", {
      program: currentProgram.program?.Name,
      competence: competence?.Name,
      skill: skill?.Name,
    });

    EventRegister.emit("evidence/add-skill-evidence", data);
  };

  const goToProgressCheck = (item: ProgressCheck): void => {
    const progressCheckID = item.ID;

    FirebaseService.logEvent("program_progress_check_pressed", {
      program: currentProgram.program?.Name,
      progressCheck: item.Name,
    });

    history.push(`/dashboard/program/${params.programId}/progress-check/${progressCheckID}`);
  };

  const setupDetails = (): void => {
    FirebaseService.logEvent("program_setup_pressed", {
      program: currentProgram.program?.Name,
    });

    if (ProgramUtils.isTelephoneTriageModule(params.programId)) {
      history.push(`/dashboard/program/${program.ID}/role-team-setup`);
    } else {
      history.push(`/dashboard/program/${params.programId}/details`);
    }
  };

  const logDocumentExport = async (pages: number): Promise<void> => {
    await FirebaseService.logEvent("evidence_exported", { pages });
  };

  const generatePdfDocument = useCallback(
    async (exportType: string, program?: ProgramData): Promise<void> => {
      try {
        const doc = pdf();
        let pdfGenerated = false;

        let evidenceToExport: IEvidence[] = [];

        // All evidence for the specific program selected - e.g. HART, CHUB, etc.
        const copy = _.cloneDeep(programEvidence);

        evidenceToExport = _.sortBy(copy, (item) => new Date(item.date).getTime()).reverse();

        evidenceToExport = evidenceToExport.filter((item: IEvidence) => {
          const evidenceJSON = item.evidenceJSON;
          const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

          return onHoldReset !== 1 && item.draft !== true && item.private !== true;
        });

        if (currentProgramCycle) {
          evidenceToExport = evidenceToExport.filter((item: IEvidence) => {
            return item.evidenceJSON?.programInfo?.programCycle === currentProgramCycle;
          });
        }

        doc.updateContainer(
          <ExportedPDF
            evidence={evidenceToExport}
            user={user.contents}
            allUserRoles={allUserRoles}
            isHCPC={exportType === IExportType.HCPC}
            summaryOfWork={
              exportType === IExportType.HCPC && user.state === "hasValue" ? user.contents?.summaryOfWork : undefined
            }
            personalStatement={
              exportType === IExportType.HCPC && user.state === "hasValue"
                ? user.contents?.personalStatement
                : undefined
            }
            isMajorIncident={exportType === IExportType.MAJOR_INCIDENT}
            program={program}
            usageData={usageData}
            userPrograms={userPrograms}
            programProgressCheckData={programProgressCheckData}
          />
        );

        doc.on("change", async () => {
          if (!pdfGenerated && doc.container.document && doc.container.document.children.length > 0) {
            pdfGenerated = true;
            const blob = await doc.toBlob();

            try {
              await logDocumentExport(doc.container.document.children.length);
            } catch (error) {
              console.log(error);
            }

            const username = user.contents.userData.userName?.replace(/\s/gi, "-") || "Untitled";
            const programName = program?.Title?.replace(/\s/gi, "-") || "Program";
            const filename = `${username}-${programName}-Portfolio-${format(new Date(), "dd-MM-yyyy")}`;

            if (await DataController.isWebAsync()) {
              saveAs(blob, filename.trim().length > 0 ? filename.trim() : "Untitled");
            } else {
              const filePath = await WriteBlob({
                path: `ParaFolio/Exports/${DataController.getFolderFromExportType(exportType)}/${filename.trim().length > 0 ? filename.trim() : "Untitled"}.pdf`,
                blob,
                directory: Directory.Library,
                recursive: true,
              });

              FileOpener.open({
                filePath,
              });
            }

            doc.updateContainer(<Document />);
            setExportingEvidence(false);
            setCurrentExportType("");
          }
        });
      } catch (error) {
        console.log(error);
        setExportingEvidence(false);
        setCurrentExportType("");
      }
    },
    [programEvidence, user, programProgressCheckData]
  );

  const exportPressed = useCallback(
    async (exportType: string, program: ProgramData): Promise<void> => {
      try {
        if (user.state === "hasValue" && user.contents) {
          setExportingEvidence(true);
          setCurrentExportType(exportType);
          await generatePdfDocument(exportType, program);
        }
      } catch (error) {
        console.log(error);
        window.alert("An error occurred while exporting, please try again.");
        setExportingEvidence(false);
        setCurrentExportType("");
      }
    },
    [generatePdfDocument, user]
  );

  const evidenceCardClicked = (item: IEvidence): void => {
    const _evidence = _.pickBy(_.cloneDeep(item), _.identity);

    const evidenceJSON = _evidence.evidenceJSON;
    const progressCheckCompetenceID = evidenceJSON?.programInfo?.progressCheckCompetenceID;

    const programID = program?.ID;
    const progressCheckID = evidenceJSON?.programInfo?.progressCheckID;

    if (ProgramUtils.isWellbeingActivity(progressCheckCompetenceID)) {
      history.push(
        `/dashboard/program/${programID}/progress-check/${progressCheckID}/wellbeing-activity/${progressCheckCompetenceID}/results/${_evidence.id}`
      );
    } else {
      history.push(`/dashboard/program/${params.programId}/read/${_evidence.id}`);
    }
  };

  const openCertificate = async (item: IUserProgramCertificate): Promise<void> => {
    try {
      setViewingCertificate(true);
      setViewingCertificateFilename(item.certificate?.name || "");

      const accessParams: AWSAccessParams = await AWSService.generateSTSToken(user.contents);

      const file = await AWSService.openCertificateFromServer(user.contents, accessParams, item.certificate);

      if (await DataController.isWebAsync()) {
        const url = window.URL.createObjectURL(
          new Blob([new Uint8Array(file.Body.data)], {
            type: file.ContentType,
          })
        );
        window.open(url, "_blank");
      } else {
        const blob = new Blob([new Uint8Array(file.Body.data)], {
          type: file.ContentType,
        });

        const filePath = await WriteBlob({
          path: `ParaFolio/Certificates/${item.certificate?.name}.${mime.getExtension(file.ContentType)}`,
          blob,
          directory: Directory.Library,
          recursive: true,
        });

        FileOpener.open({
          filePath,
        });
      }

      setViewingCertificate(false);
      setViewingCertificateFilename("");
    } catch (error) {
      console.log(error);
      window.alert("An error occurred when opening certificate");
      setViewingCertificate(false);
      setViewingCertificateFilename("");
    }
  };

  const getCurrentProgressCheckData = (allData: AllProgressCheckStatuses | null, check: ProgressCheck) => {
    if (allData) {
      const data = allData.pCs.find((item) => item.pCId === check.ID);

      return data;
    }

    return;
  };

  const renderPrimaryRoleTitle = () => {
    if (user.state !== "hasValue" || !user.contents) {
      return null;
    }

    const selectedRoleID = ProgramUtils.getSelectedRoleIdForEPRRProgram(user.contents, program.ID);
    if (!selectedRoleID) {
      return null;
    }

    const eprrProgram = user.contents.programRoles?.find((role) => role.programID === program.ID);
    const primaryRoleName = eprrProgram?.programRoles[0]?.roleName ?? "";

    if (!primaryRoleName) {
      return null;
    }

    return <div className="text-acc-15px font-acc-700 text-grey-90">{primaryRoleName}</div>;
  };

  const renderMandatoryStandards = () => {
    if (user.state === "hasValue") {
      const selectedRoleID = ProgramUtils.getSelectedRoleIdForEPRRProgram(user.contents, program.ID);
      const mandatoryStandards = ProgramUtils.getMandatoryStandardsFromProgram(currentProgram.program, selectedRoleID);

      if (mandatoryStandards && mandatoryStandards.length > 0) {
        return (
          <>
            <div className="mt-3 text-acc-13px font-acc-400 text-grey-90">Mandatory Standards</div>
            {mandatoryStandards.map((standard) => {
              const usageResult = usageData.find((item) => item.compId === standard.id);
              const percentage = usageResult ? Number(usageResult.percentage) : 0;
              return (
                <NOSOverviewItem
                  key={standard.id}
                  title={standard.name}
                  subtitle={`${retrieveEvidenceCount(standard.id)} evidence`}
                  description={standard.description}
                  percentage={Math.floor(percentage)}
                  programId={program.ID}
                  buttonPressed={() => {
                    history.push(`/dashboard/program/${params.programId}/standard/${standard.id}`);
                  }}
                />
              );
            })}
          </>
        );
      } else {
        return null;
      }
    } else {
      return null;
    }
  };

  const renderOptionalStandards = () => {
    if (user.state === "hasValue") {
      const optionalStandards = ProgramUtils.getOptionalStandardsFromUser(user.contents, currentProgram.program);

      if (optionalStandards && optionalStandards.length > 0) {
        return (
          <>
            <div
              className={
                ProgramUtils.showOptionalStandardsAsSeparateSection(program.ID)
                  ? "mt-11 text-acc-15px font-acc-700 text-grey-90"
                  : "mt-5 text-acc-13px font-acc-400 text-grey-90"
              }
            >
              Optional Standards
            </div>
            {optionalStandards.map((standard) => {
              const usageResult = usageData.find((item) => item.compId === standard.id);
              const percentage = usageResult ? Number(usageResult.percentage) : 0;
              return (
                <NOSOverviewItem
                  key={standard.id}
                  title={standard.name}
                  subtitle={`${retrieveEvidenceCount(standard.id)} evidence`}
                  description={standard.description}
                  percentage={Math.floor(percentage)}
                  programId={program.ID}
                  buttonPressed={() => {
                    history.push(`/dashboard/program/${params.programId}/standard/${standard.id}`);
                  }}
                />
              );
            })}
          </>
        );
      } else {
        return null;
      }
    } else {
      return null;
    }
  };

  const renderSecondaryRoleStandards = () => {
    if (user.state !== "hasValue" || !ProgramUtils.showSecondaryRoleStandardsInProgramHomePage(program.ID)) {
      return null;
    }

    const secondaryRoles = ProgramUtils.getSelectedSecondaryRolesForEPRRProgram(user.contents, program.ID);
    const primaryRoleId = ProgramUtils.getSelectedRoleIdForEPRRProgram(user.contents, program.ID);
    const primaryStandardIds = ProgramUtils.getPrimaryRoleStandardIds(user.contents!, currentProgram.program);

    if (!secondaryRoles.length) {
      return null;
    }

    return secondaryRoles.map((secondaryRole) => {
      const roleId =
        secondaryRole.roleID ||
        ProgramUtils.findSecondaryRoleIdByName(currentProgram.program, primaryRoleId, secondaryRole.roleName);

      const { mandatory: mandatoryStandards } = ProgramUtils.getSecondaryRoleStandardsExcludingPrimary(
        currentProgram.program,
        roleId,
        primaryStandardIds
      );

      if (!mandatoryStandards.length) {
        return null;
      }

      return (
        <div
          key={secondaryRole.roleID || secondaryRole.roleName}
          className="mt-6 pt-5"
        >
          <div className="text-acc-15px font-acc-700 text-grey-90">{secondaryRole.roleName}</div>
          {mandatoryStandards.length > 0 && (
            <>
              <div className="mt-3 text-acc-13px font-acc-400 text-grey-90">{`Mandatory Standards`}</div>
              {mandatoryStandards.map((standard) => {
                const usageResult = usageData.find((item) => item.compId === standard.id);
                const percentage = usageResult ? Number(usageResult.percentage) : 0;
                return (
                  <NOSOverviewItem
                    key={`${secondaryRole.roleName}-mandatory-${standard.id}`}
                    title={standard.name}
                    subtitle={`${retrieveEvidenceCount(standard.id)} evidence`}
                    description={standard.description}
                    percentage={Math.floor(percentage)}
                    programId={program.ID}
                    buttonPressed={() => {
                      history.push(`/dashboard/program/${params.programId}/standard/${standard.id}`);
                    }}
                  />
                );
              })}
            </>
          )}
        </div>
      );
    });
  };

  const retrieveEvidenceCount = (idToMatch: string) => {
    let evidenceCount = 0;
    const filtererEvidence = programEvidence
      .filter((item) => !item.draft) // filter out drafts
      .filter((item) => new Date(item.date) >= subYears(new Date(), 2)); // filter out expired evidence
    filtererEvidence.forEach((evidence) => {
      try {
        const parsed = evidence.evidenceJSON;
        if (Array.from(parsed.Comps).some((item: any) => item.id === idToMatch)) evidenceCount += 1;
      } catch (error) {
        console.log("Failed to update evidence count");
      }
    });

    return evidenceCount;
  };

  const addEvidenceButtonDisabled = (): boolean => {
    if (pauseAndHold) {
      return true;
    }

    if (
      ProgramUtils.isPreceptorship(currentProgram.program?.ID) ||
      ProgramUtils.isNQPProgram(currentProgram.program?.ID)
    ) {
      const programComplete = ProgramUtils.isProgramCompleteNQP(
        currentProgram.progressChecks,
        allProgressCheckData.contents,
        evidence.contents,
        currentProgram.program?.ID
      );

      if (programComplete) {
        return true;
      } else if (currentProgram.progressChecks.length > 0) {
        const array: boolean[] = [];

        currentProgram.progressChecks.forEach((item) => {
          const locked = ProgramUtils.checkIfProgressCheckIsLocked(
            item,
            currentProgram.progressChecks,
            programEvidence,
            programProgressCheckData,
            program.ID,
            fastTrackActive,
            pauseAndHold
          );

          const lockedByMentorAddedEvidence = ProgramUtils.checkIfMenteeCanAddEvidence(
            item,
            programEvidence,
            ProgramUtils.useNewMenteeEvidenceImplementation(program.ID)
          );

          array.push(locked || lockedByMentorAddedEvidence);
        });

        return !array.includes(false);
      }
    }

    // TODO: Check with Fabian how LAS progress checks are handled

    if (ProgramUtils.isUOCApprenticeship(currentProgram.program?.ID)) {
      if (isUOCProgramLocked) return true;
    }

    return false;
  };

  const updateProgressCheckCycle = async (): Promise<void> => {
    try {
      setUpdatingProgressCheckCycle(true);
      const updated = await ProgressCheckService.updateProgressCheckCycle(
        user.contents,
        program?.ID,
        (currentProgramCycle ?? 1) + 1
      );

      updated && (await refreshProgressCheckData(user.contents));
    } catch (error) {
      console.log(error);
      window.alert("An error occurred when updating progress check cycle");
    } finally {
      setUpdatingProgressCheckCycle(false);
    }
  };

  const programStartDate = useMemo(() => {
    const programStart = getProgramStartDate(program?.ID, program?.Subscription ?? []);
    const userStart = ProgramUtils.getUsersStartDate(user.contents, program.ID);

    if (
      ProgramUtils.showStartDateInDetailsPage(program, program.ID) ||
      ProgramUtils.showStartDateInDetailsCard(program.ID)
    ) {
      return userStart;
    }

    return programStart;
  }, [program?.ID, program?.Subscription, user.contents]);

  const skillsForRole =
    (currentProgram.program &&
      (currentProgram.program as ProgramData)?.Roles?.find((role) => role.Name === userRoles[0])?.Skills) ??
    [];

  const sortedSkillsForRole = _.sortBy(skillsForRole, "Name");

  const requiredSkillsForRole = skillsForRole
    .filter((skill: ProgramSkill) => parseInt(skill.numberRequiredInDuration ?? 0, 10) > 0)
    .filter((skill: ProgramSkill) => !skill.HideRequiredSkillCard);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{
            maxWidth: DataController.isWeb() ? 980 : undefined,
            height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
          }}
        >
          <IonButtons slot="start">
            <IonBackButton
              className="header-back-buttons"
              defaultHref="/dashboard"
              text={DataController.getBackIconText()}
              icon={DataController.getBackIconType()}
              style={{
                marginLeft: Capacitor.getPlatform() === "android" ? 8 : 0,
                "--icon-font-size": Capacitor.getPlatform() === "android" ? "24px" : "30px",
              }}
            />
          </IonButtons>
          <HeaderTitle>{(currentProgram.program?.["Short Title"] || currentProgram.program?.Title) ?? ""}</HeaderTitle>
          <IonButtons slot="end">
            <IonButton
              disabled={isRefreshingProgramData}
              className="header-button"
              mode="ios"
              onClick={refreshUserProgramData}
            >
              {isRefreshingProgramData ? (
                <IonSpinner className="h-[20px] w-[44px] text-white" />
              ) : (
                <img
                  src={Icon_Refresh}
                  className="headerIcon"
                />
              )}
            </IonButton>
            {currentProgram.infoBody && (
              <IonButton
                className="header-button"
                mode="ios"
                onClick={() => infoClicked()}
              >
                <img
                  src={Button_Info_White}
                  className="headerIcon"
                />
              </IonButton>
            )}
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="page-background">
        <InfoModal
          visible={infoModalVisible}
          closeModal={() => setInfoModalVisible(false)}
          body={currentProgram.infoBody}
          program={currentProgram.program}
        />
        <EvidenceContainer style={{ paddingTop: 0, paddingBottom: 0, alignSelf: "center" }}>
          {ProgramUtils.showProgramDetailsCard(currentProgram.program?.ID) && (
            <>
              {ProgramUtils.forceUserDetailsSetup(program.ID) && !isSetup ? (
                <ProgramInfoCard
                  title="Set up your details"
                  subTitle={currentProgram.program?.DetailsDescription ?? undefined}
                >
                  <ProgramCardButton
                    buttonPressed={() => setupDetails()}
                    buttonText="Set up details"
                    callToAction
                  />
                </ProgramInfoCard>
              ) : (
                <DetailsCard
                  isPreceptor={false}
                  name={user.contents?.name}
                  email={user.contents?.email}
                  preceptors={ProgramUtils.mapPreceptorNames(currentProgressCheckData?.mentors, program.MentorTitle)}
                  region={ProgramUtils.mapRegionNames(user.contents?.userData.Regions, program.ID)}
                  ESRNumber={ProgramUtils.mapESRNumber(currentProgram.program, user.contents?.subscriptions)}
                  showExportButton
                  isExporting={exportingEvidence}
                  showBetaLabel={program?.showBetaLabel}
                  trust={program?.Trust}
                  exportPressed={() => exportPressed(IExportType.PROGRAM, program)}
                  exportButtonText={`Export ${program["Short Title"] ?? program.Title}`}
                  preceptorsText={ProgramUtils.getPreceptorsText(program.MentorNamingConvention, program.MentorTitle)}
                  fastTrackLabel={ProgramUtils.getFastTrackLabel(programModuleStatus)}
                  pauseLabel={ProgramUtils.getPauseLabel(programModuleStatus)}
                  showPauseInfo={ProgramUtils.showPauseInfo(programModuleStatus)}
                  showFastTrackInfo={ProgramUtils.showFastTrackInfo(programModuleStatus)}
                  pauseDescription={ProgramUtils.getPauseDescription(programModuleStatus, program)}
                  fastTrackDescription={ProgramUtils.getFastTrackDescription(programModuleStatus, program)}
                  trustBackgroundColourVariants={TRUST_BACKGROUND_COLOUR_VARIANTS}
                  startDate={programStartDate ?? undefined}
                  showEditButton={ProgramUtils.showProgramDetailsEditButton(program)}
                  editPressed={() => setupDetails()}
                  area={ProgramUtils.getEprrArea(user.contents?.programRoles, program.ID) || undefined}
                  showAbsenceConversation={ProgramUtils.hasAbsenceConversation(
                    allProgressCheckData.contents,
                    program.ID
                  )}
                  absenceConversationPressed={() =>
                    history.push(`/dashboard/program/${params.programId}/absence-conversation/`)
                  }
                  roles={userRoles.length > 0 ? userRoles.join(", ") : undefined}
                  hidePreceptors={ProgramUtils.shouldHidePreceptorsInDetailsCard(program.ID)}
                  hideStartDate={ProgramUtils.hideStartDateInDetailsCard()}
                  team={ProgramUtils.getNQPUserTeam(user.contents, program.ID) || undefined}
                  appraisalDate={ProgramUtils.getUsersAppraisalDate(user.contents, program.ID) || undefined}
                  courseID={ProgramUtils.getNQPUsersCourseId(user.contents, program.ID) || undefined}
                  level={ProgramUtils.getProgramLevel(user.contents?.programRoles, program.ID) || undefined}
                />
              )}
            </>
          )}
          {(ProgramUtils.isPreceptorship(currentProgram.program?.ID) ||
            ProgramUtils.isNQPProgram(currentProgram.program?.ID)) &&
            ProgramUtils.isProgramCompleteNQP(
              currentProgram.progressChecks,
              allProgressCheckData.contents,
              evidence.contents,
              currentProgram.program?.ID
            ) && (
              <ProgramInfoCard
                title="Congratulations!"
                isHTMLSubtitle
                subTitle={
                  program?.CompletionText ??
                  `You have completed your ${currentProgram.program?.Title} program! Your evidence will be accessible from here for future reference. Please export your data for your own records.`
                }
              >
                <IonImg
                  className="h-[112px] w-[160px] self-center"
                  src={Image_Completion}
                />
                {ProgramUtils.isSPAPRP(program?.ID) && (
                  <IonButton
                    className="program-card-button-cta mt-4 flex w-full max-w-button self-center"
                    onClick={() => updateProgressCheckCycle()}
                    disabled={updatingProgressCheckCycle}
                  >
                    {updatingProgressCheckCycle ? <IonSpinner className="size-6 text-white" /> : "Move to next cycle"}
                  </IonButton>
                )}
              </ProgramInfoCard>
            )}
          {isUOCProgramComplete && (
            <ProgramInfoCard
              title="Congratulations!"
              subTitle={program.CompletionText ?? "Your portfolio has been approved!"}
            >
              <IonImg
                className="h-[112px] w-[160px] self-center"
                src={Image_Completion}
              />
            </ProgramInfoCard>
          )}
          {ProgramUtils.hasSetup(program?.ID) && (
            <>
              {isSetup ? (
                ProgramUtils.useDetailsCardInsteadOfProgramInfoCard(params.programId) ? (
                  <div className="flex justify-center">
                    <div className="accountCardInner">
                      {ProgramUtils.isTelephoneTriageModule(program?.ID) ? (
                        <LasDetails
                          exportPressed={exportPressed}
                          exportingEvidence={exportingEvidence}
                          currentExportType={currentExportType}
                        />
                      ) : (
                        <DetailsContentCollapsible
                          title={user && user.state === "hasValue" ? user.contents?.name ?? "" : ""}
                          subTitle={user && user.state === "hasValue" ? user.contents?.email ?? undefined : undefined}
                          program={program}
                        >
                          {ProgramUtils.showStartDateInDetailsCard(program.ID) && programStartDate && (
                            <div className="mb-[4px] mt-[8px]">
                              <div className="text-acc-13px leading-[1.54] tracking-[-0.1px] text-grey-70">
                                {"Start date"}
                              </div>
                              <div className="text-acc-17px leading-[1.53] tracking-[-0.3px] text-grey-90">
                                {format(new Date(programStartDate), "d MMMM yyyy")}
                              </div>
                            </div>
                          )}
                          <ProgramRolesDisplay
                            userRoles={userRoles}
                            secondaryRoles={ProgramUtils.getSecondaryRoleNamesForProgram(
                              user.contents,
                              currentProgram.program.Name,
                              program?.ID
                            )}
                            programId={program?.ID}
                          />
                          {!ProgramUtils.hideCertificatesInDetailsCard(program?.ID) &&
                            programCertificates.map((certificate: IUserProgramCertificate) => (
                              <ProgramCertificatesDisplay
                                certificate={certificate}
                                buttonPressed={() => openCertificate(certificate)}
                                key={certificate.certificate?.certificateType}
                                viewingCertificate={viewingCertificate}
                                viewingFilename={viewingCertificateFilename}
                                roles={userRoles}
                                programID={program?.ID}
                              />
                            ))}
                          {!!ProgramUtils.getEprrArea(user.contents?.programRoles, program.ID) && (
                            <div className="mb-[4px] mt-[8px]">
                              <div className="text-acc-13px leading-[1.54] tracking-[-0.1px] text-grey-70">
                                {"Area"}
                              </div>
                              <div className="text-acc-17px leading-[1.53] tracking-[-0.3px] text-grey-90">
                                {ProgramUtils.getEprrArea(user.contents?.programRoles, program.ID)}
                              </div>
                            </div>
                          )}
                          {!!ProgramUtils.getProgramLevel(user.contents?.programRoles, program.ID) && (
                            <div className="mb-[4px] mt-[8px]">
                              <div className="text-acc-13px leading-[1.54] tracking-[-0.1px] text-grey-70">
                                {"Level"}
                              </div>
                              <div className="text-acc-17px leading-[1.53] tracking-[-0.3px] text-grey-90">
                                {ProgramUtils.getProgramLevel(user.contents?.programRoles, program.ID)}
                              </div>
                            </div>
                          )}
                          {ProgramUtils.showDeclarationOfObligationsTickbox(program.ID) && declarationDate !== "" && (
                            <div className="mb-[4px] mt-[8px]">
                              <div className="text-acc-13px leading-[1.54] tracking-[-0.1px] text-grey-70">
                                {"Declaration of Obligations confirmed"}
                              </div>
                              <div className="text-acc-17px leading-[1.53] tracking-[-0.3px] text-grey-90">
                                {format(new Date(declarationDate), "d MMMM yyyy")}
                              </div>
                            </div>
                          )}
                          {!!ProgramUtils.mapESRNumber(currentProgram.program, user.contents?.subscriptions) && (
                            <div className="mb-[4px] mt-[8px]">
                              <div className="text-acc-13px leading-[1.54] tracking-[-0.1px] text-grey-70">
                                {"ESR Number"}
                              </div>
                              <div className="text-acc-17px leading-[1.53] tracking-[-0.3px] text-grey-90">
                                {ProgramUtils.mapESRNumber(currentProgram.program, user.contents?.subscriptions)}
                              </div>
                            </div>
                          )}
                          <div className="mt-3 flex gap-2">
                            <IonButton
                              className="program-card-button h-[50px] flex-1"
                              onClick={() => exportPressed(IExportType.PROGRAM, program)}
                            >
                              {exportingEvidence && currentExportType === IExportType.PROGRAM ? (
                                <IonSpinner className="text-cta-blue" />
                              ) : (
                                <>
                                  <IonImg
                                    className="h-[40px] w-[40px]"
                                    src={Icon_Export_Blue}
                                  />
                                  <div className="acc-underline text-[16px] font-semibold text-cta-blue">
                                    {"Download"}
                                  </div>
                                </>
                              )}
                            </IonButton>
                            <IonButton
                              className="program-card-button h-[50px] flex-1"
                              onClick={() => setupDetails()}
                            >
                              <IonImg
                                className="mr-1 h-[24px] w-[24px]"
                                src={Icon_Pencil}
                              />
                              <div className="acc-underline text-[16px] font-semibold text-cta-blue">Edit</div>
                            </IonButton>
                          </div>
                        </DetailsContentCollapsible>
                      )}
                    </div>
                  </div>
                ) : (
                  <ProgramInfoCard
                    title={user ? `${user.state === "hasValue" ? user.contents?.name : ""}` : "Your details"}
                    subTitle={user ? (user.state === "hasValue" ? user.contents?.email : "") : undefined}
                    sideButton
                    sideButtonText="View details"
                    buttonClicked={() => setupDetails()}
                  >
                    {program.Certificates &&
                      program.Certificates.length > 0 &&
                      program?.Certificates?.map((certificate: ProgramCertificate) => (
                        <MandatoryCertificateDisplay
                          buttonPressed={() => setupDetails()}
                          certificate={certificate}
                          key={certificate.Name}
                          programCertificates={programCertificates}
                        />
                      ))}
                    {programCertificates.map((certificate: IUserProgramCertificate) => (
                      <ProgramCertificatesDisplay
                        certificate={certificate}
                        buttonPressed={() => openCertificate(certificate)}
                        key={certificate.certificate?.certificateType}
                        viewingCertificate={viewingCertificate}
                        viewingFilename={viewingCertificateFilename}
                        programID={params.programId}
                      />
                    ))}
                    <ProgramRolesDisplay
                      userRoles={userRoles}
                      secondaryRoles={ProgramUtils.getSecondaryRoleNamesForProgram(
                        user.contents,
                        currentProgram.program.Name,
                        params.programId
                      )}
                      programId={params.programId}
                    />
                    <ProgramCardButton
                      buttonPressed={() => exportPressed(IExportType.PROGRAM, currentProgram.program)}
                      buttonText={`Export ${currentProgram.program.Title}`}
                    >
                      {exportingEvidence && currentExportType === IExportType.PROGRAM ? (
                        <IonSpinner className="text-cta-blue" />
                      ) : (
                        <div className="flex flex-row items-center">
                          <IonImg
                            className="w-10"
                            src={Icon_Export_Blue}
                          />
                          <div className="text-center text-[16px] font-semibold leading-[1.5] tracking-default text-cta-blue">
                            {"Download"}
                          </div>
                        </div>
                      )}
                    </ProgramCardButton>
                  </ProgramInfoCard>
                )
              ) : (
                <ProgramInfoCard
                  title="Set up your details"
                  subTitle={currentProgram.program?.DetailsDescription ?? undefined}
                >
                  <ProgramCardButton
                    buttonPressed={() => setupDetails()}
                    buttonText="Set up details"
                    callToAction
                  />
                </ProgramInfoCard>
              )}
            </>
          )}
          {currentProgram.progressChecks.length > 0 && (
            <>
              {ProgramUtils.isTelephoneTriageModule(program?.ID) ? (
                <>
                  {isSetup ? (
                    <ProgramInfoCard
                      title={ProgramUtils.getProgressCheckListTitle(program.ID)}
                      subTitle={"Complete your Courses before starting Mentoring"}
                    >
                      {currentProgram.progressChecks.map((progressCheck: ProgressCheck, i) => {
                        return (
                          <CustomProgressCheckButton
                            key={progressCheck.ID}
                            title={progressCheck.Name}
                            showSubtitle={
                              ProgramUtils.isMTS(program.ID) ||
                              (ProgramUtils.isClinicalAdvisor(program.ID) &&
                                progressCheck.ID !== PROGRESS_CHECK_IDS.LAS_CA_ONGOING_DEVELOPMENT) //  don't show subtitle for ongoing development pc in LAS CA
                            }
                            progressCheck={progressCheck}
                            programEvidence={programEvidence}
                            handleClick={() => goToProgressCheck(progressCheck)}
                            progressLabel={ProgramUtils.getProgressCheckLabelInformationForTelTriage(
                              progressCheck,
                              programEvidence,
                              currentProgram.progressChecks[i - 1]
                            )}
                          />
                        );
                      })}
                    </ProgramInfoCard>
                  ) : null}
                </>
              ) : (
                <>
                  {ProgramUtils.forceUserDetailsSetup(program.ID) && !isSetup ? null : (
                    <ProgramInfoCard
                      title={ProgramUtils.getProgressCheckListTitle(program.ID)}
                      collapsible
                      subTitle={
                        program?.ProgressCheckListSubtitle ??
                        "Please complete these in order as you progress through your program"
                      }
                      isHTMLSubtitle={program?.ProgressCheckListSubtitle !== undefined}
                    >
                      {currentProgram.progressChecks.map((progressCheck: ProgressCheck) => {
                        return (
                          <ProgressCheckButton
                            key={progressCheck.ID}
                            title={progressCheck.Name}
                            buttonPressed={() => goToProgressCheck(progressCheck)}
                            evidenceCount={ProgramUtils.getEvidenceCountForProgressCheck(
                              programCycleEvidence ?? programEvidence,
                              progressCheck.ID
                            )}
                            newCommentCount={CommentHelpers.getCommentCountForProgressCheckButton(
                              user.contents,
                              currentProgressCheckData,
                              progressCheck.ID
                            )}
                            progressLabel={
                              ProgramUtils.getProgressCheckLabelInformation(
                                progressCheck,
                                getCurrentProgressCheckData(currentProgressCheckData, progressCheck),
                                programCycleEvidence ?? programEvidence,
                                currentProgram.progressChecks,
                                currentProgressCheckData,
                                program.ID,
                                fastTrackActive,
                                pauseAndHold
                              ) as any
                            }
                          />
                        );
                      })}
                    </ProgramInfoCard>
                  )}
                </>
              )}
            </>
          )}
          {(program.ShowConfidenceLevels || ProgramUtils.showConfidenceLevelGraph(params.programId)) && (
            <ProgramInfoCard
              title={program.ConfidenceLevelsTitle ?? "Confidence levels"}
              subTitle={StringUtils.getConfidenceLevelsHeader(confidenceLevelData, program?.Title, program?.ID)}
            >
              {confidenceLevelData && (
                <ConfidenceLevelGraph
                  data={confidenceLevelData}
                  labels={program.ConfidenceLevelGraphLabels ?? undefined}
                />
              )}
            </ProgramInfoCard>
          )}
          {programSkills.requiredSkills?.map((skill: ProgramSkill) => (
            <RequiredSkillCard
              buttonPressed={(competence) => addRequiredSkillEvidence(competence, skill)}
              competences={currentProgram.competences}
              key={skill.ID}
              programEvidence={programEvidence}
              skill={skill}
            />
          ))}
          {ProgramUtils.isCHUB(program.ID) && isSetup && (
            <ProgramInfoCard
              title={"Ongoing Development"}
              subTitle={
                "Complete a minimum of 2 CPD evidences each month - either Peer Review with Peer Review LO set or General"
              }
            >
              <MonthlyEvidenceBreakdown
                evidence={programEvidence}
                programId={program.ID}
                generalCompId={COMPETENCY_IDS.LAS_CHUB_ONGOING_DEVELOPMENT_GENERAL_COMPETENCY}
                paraPassLearningCompId={COMPETENCY_IDS.LAS_CA_ONGOING_DEVELOPMENT_PARAPASS_LEARNING_COMPETENCY}
              />
            </ProgramInfoCard>
          )}
          {ProgramUtils.isClinicalAdvisor(program.ID) && isSetup && (
            <ProgramInfoCard
              title={"Ongoing Development"}
              subTitle={
                "Complete a minimum of 2 CPD evidences each month - either Peer Review, ParaPass Learning or General CPD"
              }
            >
              <MonthlyEvidenceBreakdown
                evidence={programEvidence}
                programId={program.ID}
                generalCompId={COMPETENCY_IDS.LAS_CA_ONGOING_DEVELOPMENT_GENERAL_COMPETENCY}
                paraPassLearningCompId={COMPETENCY_IDS.LAS_CA_ONGOING_DEVELOPMENT_PARAPASS_LEARNING_COMPETENCY}
              />
            </ProgramInfoCard>
          )}
          {ProgramUtils.isSPAPRP(program.ID) &&
            ProgramUtils.userRoleHasSkills(userRoles, currentProgram.program) &&
            isSetup && (
              <ProgramInfoCardButton
                title={ProgramUtils.getSkillsCardTitle(program.SkillsTitle)}
                subTitle={ProgramUtils.getSkillsCardSubTitle(program.SkillsSubTitle)}
                buttonClicked={() => {
                  history.push(`/dashboard/program/${params.programId}/skills`);
                }}
              >
                <ProgramSkillsCard
                  sortedSkillsForRole={sortedSkillsForRole}
                  programEvidence={programEvidence}
                  program={currentProgram.program}
                />
              </ProgramInfoCardButton>
            )}
          {ProgramUtils.isNwasARC(program.ID) &&
            ProgramUtils.userRoleHasSkills(userRoles, currentProgram.program) &&
            isSetup && (
              <ProgramInfoCardButton
                title="Skills"
                subTitle={currentProgram.program?.SkillsCardSubtitle ?? undefined}
                buttonClicked={() => history.push(`/dashboard/program/${params.programId}/skills`)}
                buttonPosition="top"
                collapseBottomPadding
              >
                <ProgramSkillsRAGCard
                  sortedSkillsForRole={sortedSkillsForRole}
                  programEvidence={programEvidence}
                  program={currentProgram.program}
                />
              </ProgramInfoCardButton>
            )}
          {ProgramUtils.isUOCApprenticeship(program.ID) && ProgramUtils.programHasSkills(program) && (
            <ProgramSkillsMap
              program={program}
              currentLevel={ProgramUtils.getSkillLevelForProgram(user.contents, program)}
            />
          )}
          {ProgramUtils.showSkillsInProgramHomePage(currentProgram.program?.ID) &&
            ProgramUtils.userRoleHasSkills(userRoles, currentProgram.program) &&
            isSetup && (
              <>
                {requiredSkillsForRole.length > 0 && (
                  <>
                    {requiredSkillsForRole.map((skill: ProgramSkill) => (
                      <ProgramRoleRequiredSkillCard
                        program={currentProgram.program}
                        skill={skill}
                        key={skill.ID}
                        buttonPressed={() => addProgramEvidence({ type: "skill", preselectedSkill: skill })}
                        buttonPressedNonCompliant={() =>
                          addProgramEvidence({ type: "skill", preselectedSkill: skill, nonCompliant: true })
                        }
                        programEvidence={programEvidence}
                        programStartDate={programStartDate}
                      />
                    ))}
                  </>
                )}
              </>
            )}
          {ProgramUtils.showLearningObjectivesCard(params.programId, user.contents) &&
            ProgramUtils.hasLearningObjectives(program, userRoles) && (
              <ProgramInfoCardButton
                title={ProgramUtils.getLearningObjectivesCardTitle(program)}
                buttonClicked={() => history.push(`/dashboard/program/${params.programId}/learning-outcomes`)}
              >
                <NQPLearningOutcomesDisplay program={program} />
              </ProgramInfoCardButton>
            )}
          {ProgramUtils.showMedicalImagingCard(params.programId) && isSetup && (
            <ProgramInfoCardButton
              title="Medical Imaging"
              buttonClicked={() => history.push(`/dashboard/program/${params.programId}/medical-imaging`)}
            >
              <MedicalImagingDisplay program={program} />
            </ProgramInfoCardButton>
          )}
          {ProgramUtils.showWPBACard(params.programId) && isSetup && program["WPBA Forms"] && (
            <ProgramInfoCardButton
              title="WPBA Forms"
              buttonClicked={() => history.push(`/dashboard/program/${params.programId}/wpba-forms`)}
              buttonPosition="top"
              collapseBottomPadding
            >
              <WPBACardDisplay program={program} />
            </ProgramInfoCardButton>
          )}
          {ProgramUtils.showCertificatesInProgramHomePage(currentProgram.program?.ID) &&
            ProgramUtils.userRoleHasCertificates(userRoles, currentProgram.program) &&
            isSetup && (
              <>
                <ProgramInfoCardButton
                  title={currentProgram.program?.CertificateListHeader ?? "Certificates"}
                  buttonClicked={() => history.push(`/dashboard/program/${params.programId}/certificates`)}
                  buttonPosition="top"
                  collapseBottomPadding
                >
                  <ProgramCertificatesCardDisplay program={program} />
                </ProgramInfoCardButton>
              </>
            )}
          {ProgramUtils.showMandatoryStandardsInProgramHomePage(currentProgram.program?.ID) && (
            <ProgramInfoCard
              title="NOS Overview"
              subTitle={
                isSetup
                  ? ""
                  : "After selecting your role, your mandatory standards will appear here. You will also be asked to select 2 of 6 optional standards to achieve as part of your profile."
              }
            >
              {/* Primary role stuff */}
              {renderPrimaryRoleTitle()}
              {renderMandatoryStandards()}
              {/* Secondary role stuff */}
              {renderSecondaryRoleStandards()}
              {renderOptionalStandards()}
            </ProgramInfoCard>
          )}
          {ProgramUtils.showEvidenceBlock(params.programId, user.contents) && (
            <>
              <ProgramInfoCard
                title="Evidence list"
                style={{ paddingTop: 14, paddingBottom: 8 }}
                subTitle={
                  programEvidence.length === 0
                    ? `Any evidence added ${currentProgram.program?.Title ? `as ${currentProgram.program?.Title}` : "to this program module"} will appear below.`
                    : undefined
                }
              >
                {pauseAndHold && (
                  <EvidenceDisclaimer
                    message={ProgramUtils.getProgressCheckPauseAndHoldLockedText(pauseAndHold, true)}
                  />
                )}
                <ProgramCardButton
                  buttonPressed={() =>
                    addProgramEvidence(ProgramUtils.getAddProgramEvidenceType(currentProgram.program.ID))
                  }
                  buttonText="Add new evidence"
                  icon={Icon_Add_Evidence}
                  disabled={addEvidenceButtonDisabled()}
                />
                {ProgramUtils.isMTS(program?.ID) &&
                  currentProgram.progressChecks.every((pc) =>
                    ProgramUtils.isProgressCheckApprovedBasedOnCompTrigger(programEvidence, pc["Competence/Activity"])
                  ) && (
                    <div className="mt-2 rounded-lg bg-orange-20 px-3 py-2 text-acc-12px font-acc-500 text-grey-90">
                      You can no longer add evidence now your Mentoring has been approved.
                    </div>
                  )}
                {programEvidence.length > 0 && (
                  <div className="mt-3">
                    {programEvidence.map((item) => (
                      <ProgramEvidenceCard
                        key={item.id}
                        buttonPressed={() => evidenceCardClicked(item)}
                        programEvidence={item}
                        program={currentProgram.program}
                        progressCheckLocked={ProgramUtils.checkIfProgressCheckIsLockedWithProgramCycle({
                          program: program,
                          progressChecks: currentProgram.progressChecks,
                          allProgressCheckData: allProgressCheckData.contents,
                          allEvidence: evidence.contents,
                          programID: program?.ID,
                          programCycle: currentProgramCycle,
                        })}
                      />
                    ))}
                  </div>
                )}
              </ProgramInfoCard>
            </>
          )}
          {isDebugUser && (
            <ProgramInfoCard title="Debug Info">
              <div>{`Version ${program?.version}`}</div>
            </ProgramInfoCard>
          )}
        </EvidenceContainer>
      </IonContent>
    </IonPage>
  );
};

export default ProgramHome;
